import { useMemo } from 'react';

/**
 * WaveformView — renders a seeded pseudo-random waveform that looks consistent
 * for the same URL/seed. Used as a lightweight placeholder inside track segments
 * without requiring audio decoding or network access.
 *
 * Props:
 *   seed   — string used to seed the pattern (e.g. track URL or name)
 *   width  — pixel width of the SVG
 *   height — pixel height of the SVG (default 28)
 *   color  — bar fill color (default rgba(255,255,255,0.25))
 */

function hashStr(str) {
    let h = 2166136261;
    for (let i = 0; i < (str || '').length; i++) {
        h ^= (str || '').charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function seededRand(seed) {
    let s = seed >>> 0;
    return () => {
        s = Math.imul(s ^ (s >>> 16), 0x45d9f3b);
        s = Math.imul(s ^ (s >>> 16), 0x45d9f3b);
        s = s ^ (s >>> 16);
        return (s >>> 0) / 0xffffffff;
    };
}

export default function WaveformView({ seed = '', width = 100, height = 28, color = 'rgba(255,255,255,0.25)', samples = null }) {
    // When real decoded samples are provided use them; otherwise generate a
    // seeded pseudo-random pattern so each URL always looks the same.
    const bars = useMemo(() => {
        const barW = 2;
        const gap  = 1;
        const step = barW + gap;
        const count = Math.max(4, Math.floor(width / step));

        let amplitudes;
        if (samples && samples.length > 0) {
            // For each bar, take the max amplitude from its source bin so peaks
            // are never lost when downsampling (avoids the "smoothed-out" look).
            amplitudes = new Array(count);
            for (let i = 0; i < count; i++) {
                const lo = Math.floor((i / count) * samples.length);
                const hi = Math.ceil(((i + 1) / count) * samples.length);
                let max = 0;
                for (let j = lo; j < hi; j++) {
                    if (samples[j] > max) max = samples[j];
                }
                amplitudes[i] = Math.max(0.04, max);
            }
        } else {
            const rand = seededRand(hashStr(seed));
            amplitudes = [];
            for (let i = 0; i < count; i++) {
                const t = i / count;
                const base = 0.4 + 0.35 * Math.sin(t * Math.PI * 6) + 0.15 * Math.sin(t * Math.PI * 17);
                const noise = (rand() - 0.5) * 0.35;
                amplitudes.push(Math.max(0.05, Math.min(1, base + noise)));
            }
        }
        return { amplitudes, step, barW };
    }, [seed, width, samples]);

    const { amplitudes, step, barW } = bars;
    const midY = height / 2;
    // Real waveforms get fuller height; fake get 88% to remain decorative
    const heightScale = samples ? 0.96 : 0.88;

    return (
        <svg
            width={width}
            height={height}
            style={{ display: 'block', pointerEvents: 'none' }}
            shapeRendering="crispEdges"
            aria-hidden="true"
        >
            {amplitudes.map((amp, i) => {
                // Snap everything to whole pixels — eliminates sub-pixel blurring
                const barH = Math.max(2, Math.round(amp * height * heightScale));
                const y    = Math.round(midY - barH / 2);
                return (
                    <rect
                        key={i}
                        x={i * step}
                        y={y}
                        width={barW}
                        height={barH}
                        fill={color}
                    />
                );
            })}
        </svg>
    );
}
